import React, { useState } from "react";
import PageHeader from "@src/components/common/PageHeader";
import PageContent from "@src/components/containers/PageContent";
import PageFooter from "@src/components/common/PageFooter";
import PageContainer from "@src/components/containers/PageContainer";
import SectionHeader from "@src/components/widgets/SectionHeader";
import SpinningLoader from "@src/components/common/SpinningLoader";
import { Input, useToast } from "@swordsweeper/ssi-ui-component-library";
import Tile from "@src/components/containers/Tile";
import FormContainer from "@src/components/containers/FormContainer";
import HelpRequestForm, {
  schema as HelpRequestFormSchema,
} from "@src/components/forms/HelpRequestForm";
import * as SupportApi from "@src/api/SupportApi";

const CONTACT_EMAIL =
  (window as any).djangoConfig?.SUPPORT_EMAIL || "support@vetgetrx.com";
const CONTACT_PHONE = "1-888-387-4833";

interface FormData {
  email: string;
  message: string;
}

interface FormDataController {
  setFormData: (data: FormData) => void;
}

function SupportPage(): React.JSX.Element {
  const { showToast } = useToast();
  const [questionSearch, setQuestionSearch] = useState<string | null>(null);
  const [isCreatingHelpRequest, setIsCreatingHelpRequest] =
    useState<boolean>(false);
  const [helpRequestFormDataController, setHelpRequestFormDataController] =
    useState<FormDataController | null>(null);

  const handleCreateHelpRequest = async (formData: FormData): Promise<void> => {
    setIsCreatingHelpRequest(true);
    try {
      const response = await SupportApi.createUserHelpRequest(formData);
      helpRequestFormDataController?.setFormData({ email: "", message: "" });
      showToast({
        message: "Help request submitted",
        variant: "success",
      });
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error submitting help request",
        variant: "error",
      });
    } finally {
      setIsCreatingHelpRequest(false);
    }
  };

  const handleChatClick = (): void => {
    alert("TODO");
  };

  return (
    <>
      {isCreatingHelpRequest && <SpinningLoader />}

      <div data-testid="support-page">
        <SectionHeader
          header={
            <span className="text-xl">Need Help? We're here for it!</span>
          }
        />

        <div className="flex-row flex-center padding-top-xxl">
          <div className="flex-row spaced-content-lg width-75">
            <Tile
              className="flex-1 spaced-content-v-lg"
              data-testid="help-request-tile"
            >
              <div className="font-weight-bold text-lg">How Can We Help?</div>
              <div>
                Send us a message. We'll get back to you within 24 hours.
              </div>
              <FormContainer
                form={HelpRequestForm}
                schema={HelpRequestFormSchema}
                onSubmit={handleCreateHelpRequest}
                submitButtonText="Send Message"
                onReceiveFormDataController={setHelpRequestFormDataController}
              />
            </Tile>
            <div className="flex-1 flex-column spaced-content-v-lg">
              <Tile
                className="spaced-content-v-lg"
                data-testid="contact-info-tile"
              >
                <div className="font-weight-bold text-lg">
                  How to Contact Us
                </div>
                <div>
                  Customer Care is available from 9am-5pm CST, Mon-Fri.
                  Saturday and Sunday: Closed.
                </div>
                <div>
                  For questions about your or order, contact your representative
                  at the distributor.
                </div>
                <ul>
                  <li>
                    <a
                      href={`mailto:${CONTACT_EMAIL}`}
                      data-testid="support-email-link"
                    >
                      {CONTACT_EMAIL}
                    </a>
                  </li>
                  <li>{CONTACT_PHONE}</li>
                  <li>
                    <a onClick={handleChatClick} data-testid="live-chat-link">
                      Chat with a live agent now
                    </a>
                  </li>
                </ul>
              </Tile>

              <Tile
                className="spaced-content-v-lg"
                data-testid="knowledge-base-tile"
              >
                <div className="font-weight-bold text-lg">
                  Search Our Knowledge Base
                </div>
                <div>Most of your questions can be answered right here.</div>
                <Input
                  defaultValue=""
                  onChange={(value: string) => setQuestionSearch(value)}
                  value={questionSearch || ""}
                  width="100%"
                  placeholder="Ask your question here..."
                  data-testid="knowledge-base-search-input"
                />
              </Tile>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SupportPage;
